import React, { useState, useEffect, useRef } from "react";
import { X, Check, Bell, User } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { BASE_URL } from "../utils/constant";
const NotificationSidePopUp = ({ isOpen, onClose, notifications, markAllAsRead, markAsRead, currentUser }) => {
    const [activeTab, setActiveTab] = useState("All");
    const [shouldRender, setShouldRender] = useState(false);
    const [members, setMembers] = useState([]);
    const navigate = useNavigate();
    const sidebarRef = useRef(null);

    const organizationId = JSON.parse(localStorage.getItem("organization"))?.id;

    const fetchMembers = async () => {
        if (!organizationId) return;
        const accessToken = localStorage.getItem("access");
        try {
            const response = await fetch(
                `${BASE_URL}/org/organization/${organizationId}/members/`,
                {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${accessToken}`,
                    },
                }
            );
            const data = await response.json();
            if (data.status) {
                setMembers(data.data);
            }
        } catch (error) {
            console.error("Error fetching members:", error);
        }
    };

    useEffect(() => {
        if (isOpen) {
            setShouldRender(true);
            fetchMembers();
            // Disable body scroll when open
            document.body.style.overflow = "hidden";
        } else {
            setTimeout(() => setShouldRender(false), 300); // Wait for animation
            document.body.style.overflow = "unset";
        }
        return () => {
            document.body.style.overflow = "unset";
        };
    }, [isOpen]);

    // Handle click outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                sidebarRef.current &&
                !sidebarRef.current.contains(event.target) &&
                isOpen
            ) {
                onClose();
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [isOpen, onClose]);

    if (!shouldRender) return null;

    const tabs = ["All", "Unread", "Mentions", "System"];

    const filteredNotifications = notifications.filter((notification) => {
        console.log("current user", currentUser.user)
        if (activeTab === "All") return true;
        if (activeTab === "Unread") return !(notification.read_by || []).includes(currentUser?.user)
        if (activeTab === "Mentions") return notification.type === "mention"; // Assuming 'type' field exists or title contains mention logic
        if (activeTab === "System") return notification.type === "system" || !notification.type; // Fallback for basic notifications
        return true;
    });

    // Grouping logic
    const groupedNotifications = filteredNotifications.reduce((acc, notification) => {
        const date = new Date(notification.date);
        const today = new Date();
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);

        let key = "Earlier";
        if (date.toDateString() === today.toDateString()) {
            key = "Today";
        } else if (date.toDateString() === yesterday.toDateString()) {
            key = "Yesterday";
        }

        if (!acc[key]) acc[key] = [];
        acc[key].push(notification);
        return acc;
    }, {});

    const order = ["Today", "Yesterday", "Earlier"];

    const handleNotificationClick = (notification) => {
        if (markAsRead) markAsRead(notification.id);

        const { module, type, metadata, site_id } = notification;
        const targetSiteId = site_id;

        if (!targetSiteId) {
            console.warn("No site ID found in notification:", notification);
            onClose();
            return;
        }

        const navTo = (path) => {
            navigate(path);
            onClose();
        };

        // Redirection logic based on tabular flow
        switch (module?.toLowerCase()) {
            case "finance":
                navTo(`/site/${targetSiteId}/finance`);
                break;

            case "materials":
                if (["material_created", "materials_bulk_created", "stock_updated", "low_inventory"].includes(type)) {
                    const materialId = metadata?.material_id;
                    navTo(`/site/${targetSiteId}/materials/${materialId || ""}?activeTab=inventory`);
                } else if (["delivery_created", "delivery_marked", "part_payment"].includes(type)) {
                    const deliveryId = metadata?.delivery_id;
                    navTo(`/site/${targetSiteId}/materials/${deliveryId || ""}?activeTab=purchase`);
                } else {
                    navTo(`/site/${targetSiteId}/materials`);
                }
                break;

            case "tasks":
                // Standard tasks navigation for now as deep linking to tasks requires TaskDetails prop support in SiteHome
                navTo(`/site/${targetSiteId}/tasks`);
                break;

            case "workers":
                if (["worker_created", "attendance_marked"].includes(type)) {
                    const workerId = metadata?.worker_id;
                    navTo(`/site/${targetSiteId}/workers/${workerId || ""}`);
                } else {
                    navTo(`/site/${targetSiteId}/workers`);
                }
                break;

            case "snags":
                if (type === "snag_message") {
                    const snagId = metadata?.snag_id;
                    navTo(`/site/${targetSiteId}/snags?snagId=${encodeURIComponent(snagId || "")}`);
                } else {
                    navTo(`/site/${targetSiteId}/snags`);
                }
                break;

            default:
                // Fallback to existing logic or default routes
                if (type === "snag_message") {
                    const snagId = notification.snagId || metadata?.snag_id;
                    navTo(`/site/${targetSiteId}/snags?snagId=${encodeURIComponent(snagId || "")}`);
                } else {
                    // Fallback to basic module routes if available
                    const moduleMap = {
                        Materials: "materials",
                        Tasks: "tasks",
                        Finance: "finance",
                        Workers: "workers",
                        Snags: "snags"
                    };
                    const targetTab = moduleMap[notification.module] || "";
                    if (targetTab) {
                        navTo(`/site/${targetSiteId}/${targetTab}`);
                    } else {
                        onClose();
                    }
                }
                break;
        }
    };

    const formatTime = (dateString) => {
        if (!dateString) return "";
        const date = new Date(dateString);
        const now = new Date();
        const diffInSeconds = Math.floor((now - date) / 1000);

        if (diffInSeconds < 60) return "Just now";
        if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
        if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;

        return new Intl.DateTimeFormat("en-US", {
            day: "numeric",
            month: "short",
            hour: "numeric",
            minute: "numeric"
        }).format(date);
    };

    const getInitials = (firstName, lastName) => {
        return `${firstName?.charAt(0) || ""}${lastName?.charAt(0) || ""}`.toUpperCase() || "SM";
    };

    const getModuleTagStyles = (module) => {
        const styles = {
            Materials: "bg-blue-50 text-blue-600 border-blue-100",
            Tasks: "bg-purple-50 text-purple-600 border-purple-100",
            Snags: "bg-red-50 text-red-600 border-red-100",
            Finance: "bg-amber-50 text-amber-600 border-amber-100",
            Workers: "bg-orange-50 text-orange-600 border-orange-100",
        };
        return styles[module] || "bg-gray-50 text-gray-600 border-gray-100";
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-end">
            <div
                ref={sidebarRef}
                className="w-[420px] bg-white h-full shadow-xl flex flex-col animate-slide-in"
            >
                {/* Header */}
                <div className="flex flex-col gap-3 px-3 py-2 border-b border-gray-100">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="bg-[#F3EBF9] p-2 rounded-xl">
                                <Bell className="w-5 h-5 text-[#3B0069]" />
                            </div>
                            <div className="flex items-center gap-2">
                                <h2 className="text-xl font-bold text-gray-900">Notifications</h2>
                                <span className="bg-[#58267F] text-white text-xs font-medium px-2 py-0.5 rounded-full">
                                    {notifications.filter((n) => !(n.read_by || []).includes(currentUser?.user)).length} new
                                </span>
                            </div>
                        </div>
                        <div className="flex items-center gap-4">
                            <button
                                onClick={markAllAsRead}
                                className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-[#3B0069] transition-colors"
                            >
                                <Check className="w-4 h-4" />
                                <span className="hidden sm:inline">Mark all read</span>
                            </button>
                        </div>
                    </div>

                    {/* Tabs */}
                    <div className="flex items-center gap-2 overflow-x-auto no-scrollbar">
                        {tabs.map((tab) => (
                            <button
                                key={tab}
                                onClick={() => setActiveTab(tab)}
                                className={`px-3 py-1 rounded-full text-sm font-medium whitespace-nowrap transition-all ${activeTab === tab
                                    ? "bg-gray-100 text-gray-900 border border-transparent"
                                    : "bg-transparent text-gray-500 hover:text-gray-700 border border-transparent"
                                    }`}
                            >
                                {tab}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-2">
                    {filteredNotifications.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-full text-center p-8">
                            <div className="bg-gray-50 p-4 rounded-full mb-4">
                                <Bell className="w-8 h-8 text-gray-300" />
                            </div>
                            <h3 className="text-lg font-medium text-gray-900 mb-1">No notifications</h3>
                            <p className="text-sm text-gray-500">You're all caught up! Check back later for updates.</p>
                        </div>
                    ) : (
                        <div className="flex flex-col gap-6 p-2">
                            {order.map((group) => {
                                if (!groupedNotifications[group] || groupedNotifications[group].length === 0) return null;
                                return (
                                    <div key={group}>
                                        <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 px-2">
                                            {group}
                                        </h3>
                                        <div className="flex flex-col gap-1">
                                            {groupedNotifications[group].map((notification) => {
                                                const member = members.find(m => m.user.id === notification.actor_id);
                                                const userpic = member?.user?.userpic;
                                                const initials = member ? getInitials(member.user.first_name, member.user.last_name) : "SM";
                                                const notification_read = (notification.read_by || []).includes(currentUser?.user)
                                                const formattedMessage = notification.actor_name
                                                    ? `${notification.actor_name} ${notification.message}`
                                                    : notification.message;

                                                return (
                                                    <div
                                                        key={notification.id}
                                                        onClick={() => handleNotificationClick(notification)}
                                                        className={`group relative flex gap-4 p-3 rounded-xl cursor-pointer transition-all hover:bg-gray-50 ${!notification_read ? "bg-[#FBF9FD]" : ""}`}
                                                    >
                                                        <div className="flex-shrink-0">
                                                            {userpic ? (
                                                                <img
                                                                    src={userpic}
                                                                    alt={notification.actor_name || "User"}
                                                                    className="w-10 h-10 rounded-full object-cover ring-2 ring-gray-50"
                                                                />
                                                            ) : (
                                                                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold ${notification.type === 'system' ? 'bg-[#58267F] text-white' : 'bg-[#7C3AED] text-white'}`}>
                                                                    {initials}
                                                                </div>
                                                            )}
                                                        </div>

                                                        <div className="flex-1 min-w-0">
                                                            <div className="flex items-start justify-between gap-2">
                                                                <div>
                                                                    <div className="flex items-center gap-2">
                                                                        <p className="text-sm font-semibold text-gray-900">
                                                                            {notification.title || "Notification"}
                                                                        </p>
                                                                        {notification.module && (
                                                                            <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full border ${getModuleTagStyles(notification.module)}`}>
                                                                                {notification.module}
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                                <span className="text-xs text-gray-400 whitespace-nowrap">
                                                                    {formatTime(notification.date)}
                                                                </span>
                                                            </div>
                                                            <p className="text-sm text-gray-600 mt-1 line-clamp-3 leading-relaxed">
                                                                {formattedMessage}
                                                            </p>
                                                        </div>
                                                        {!notification_read && (
                                                            <div className="absolute top-1/2 right-2 -translate-y-1/2 w-2 h-2 bg-[#58267F] rounded-full opacity-0 group-hover:opacity-100 transition-opacity" />
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="p-3 border-t border-gray-100 bg-white">
                    <button
                        onClick={() => {
                            onClose();
                            // Navigate to full page logic if exists
                        }}
                        className="w-full py-2 text-[#58267F] font-semibold text-sm hover:bg-purple-50 rounded-xl transition-colors"
                    >
                        View all notifications
                    </button>
                </div>
            </div>
        </div>
    );
};

export default NotificationSidePopUp;
